/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneScrollRail.tsx
 * FILE_ID: JOBHUB-QA-PHONE-SCROLL-RAIL-001
 * PURPOSE: Small draggable scroll bar for the phone QA checklist.
 * LANDMINE: lane width 12 = 4px gutter + 8px --qa-phone-inset in apps/qa/src/index.css.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react'

// Thumb math is exported for the unit test. The rail component is the only UI export.
/* eslint-disable react-refresh/only-export-components */

export const QA_PHONE_RAIL_LANE_PX = 12

export type RailMetrics = { scrollTop: number; scrollHeight: number; clientHeight: number }

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

export function railThumb(metrics: RailMetrics, minThumb = 28): { visible: boolean; top: number; height: number } {
  const range = metrics.scrollHeight - metrics.clientHeight
  if (range <= 1 || metrics.clientHeight <= 0) return { visible: false, top: 0, height: 0 }
  const height = Math.max(minThumb, Math.round(metrics.clientHeight * metrics.clientHeight / metrics.scrollHeight))
  const travel = metrics.clientHeight - height
  const top = Math.round(clamp(metrics.scrollTop, 0, range) / range * travel)
  return { visible: true, top, height }
}

export function scrollTopForThumbTop(thumbTop: number, metrics: RailMetrics, thumbHeight: number) {
  const range = metrics.scrollHeight - metrics.clientHeight
  const travel = metrics.clientHeight - thumbHeight
  if (travel <= 0) return 0
  return clamp(thumbTop, 0, travel) / travel * range
}

function metricsOf(scroller: HTMLElement): RailMetrics {
  return {
    scrollTop: scroller.scrollTop,
    scrollHeight: scroller.scrollHeight,
    clientHeight: scroller.clientHeight,
  }
}

export function QaPhoneScrollRail({ scrollerRef }: { scrollerRef: RefObject<HTMLDivElement | null> }) {
  const [thumb, setThumb] = useState({ visible: false, top: 0, height: 0 })
  const drag = useRef<{ startY: number; startTop: number; height: number } | null>(null)

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const update = () => {
      const next = railThumb(metricsOf(scroller))
      setThumb((current) => (
        current.visible === next.visible && current.top === next.top && current.height === next.height
          ? current
          : next
      ))
    }
    update()
    scroller.addEventListener('scroll', update, { passive: true })
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    resize?.observe(scroller)
    const mutations = new MutationObserver(update)
    mutations.observe(scroller, { childList: true, subtree: true })
    return () => {
      scroller.removeEventListener('scroll', update)
      resize?.disconnect()
      mutations.disconnect()
    }
  }, [scrollerRef])

  if (!thumb.visible) return null

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current
    if (!scroller) return
    event.currentTarget.setPointerCapture?.(event.pointerId)
    const laneTop = event.currentTarget.getBoundingClientRect().top
    const y = event.clientY - laneTop
    const onThumb = y >= thumb.top && y <= thumb.top + thumb.height
    const startTop = onThumb ? thumb.top : y - thumb.height / 2
    if (!onThumb) scroller.scrollTop = scrollTopForThumbTop(startTop, metricsOf(scroller), thumb.height)
    drag.current = { startY: event.clientY, startTop, height: thumb.height }
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const scroller = scrollerRef.current
    if (!drag.current || !scroller) return
    const nextTop = drag.current.startTop + (event.clientY - drag.current.startY)
    scroller.scrollTop = scrollTopForThumbTop(nextTop, metricsOf(scroller), drag.current.height)
  }

  return (
    <div
      aria-hidden
      data-testid="qa-phone-scroll-rail"
      className="absolute inset-y-0 right-0 z-10 touch-none"
      style={{ width: QA_PHONE_RAIL_LANE_PX }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => { drag.current = null }}
      onPointerCancel={() => { drag.current = null }}
    >
      <div className="absolute inset-y-1 right-1 w-1 rounded-full bg-muted" />
      <div
        data-testid="qa-phone-scroll-thumb"
        className="absolute right-1 w-1 rounded-full bg-muted-foreground/50"
        style={{ top: thumb.top, height: thumb.height }}
      />
    </div>
  )
}
